import { Archive, ArrowLeft, Forum, MoreHorizontal, Pencil, Tag, Threads, Trash2, Undo2, X } from '@/components/ui/icons';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { LIMITS, type ThreadDTO } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { threadApi } from '@/api/endpoints';
import { IconButton } from '@/components/ui/Button';
import { Menu, type MenuEntry } from '@/components/ui/Popover';
import { useChannelPermissions } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { useAuth } from '@/stores/auth';
import { useServers } from '@/stores/servers';
import { useThreads } from '@/stores/threads';
import { confirm, toast } from '@/stores/ui';
import { TagChip } from '@/components/forum/ForumView';
import { ChatView } from './ChatView';

/**
 * An open thread: beside its channel on wide screens, or in place of it on
 * smaller ones (`asPage`, with a back button).
 */
export function ThreadPanel({ thread, onClose, asPage }: { thread: ThreadDTO; onClose: () => void; asPage?: boolean }) {
  const myId = useAuth((s) => s.user?.id);
  const perms = useChannelPermissions(thread.id);
  const parent = useServers((s) => s.channels[thread.parentId]);
  const parentName = parent?.name ?? '';
  // In a forum, a thread is a post (with tags).
  const isPost = parent?.type === 'FORUM';
  const noun = isPost ? 'post' : 'thread';
  const [editingTags, setEditingTags] = useState(false);
  const canManage = perms.can('MANAGE_MESSAGES') || (thread.ownerId === myId && perms.can('SEND_MESSAGES'));
  const canDelete = perms.can('MANAGE_MESSAGES');
  const [renaming, setRenaming] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const moreRef = useRef<HTMLButtonElement>(null);

  const update = async (input: { name?: string; archived?: boolean; tagIds?: string[] }) => {
    try {
      useThreads.getState().upsert([await threadApi.update(thread.id, input)]);
      return true;
    } catch (err) {
      toast.error(errorMessage(err, `Could not update the ${noun}.`));
      return false;
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: `Delete ${thread.name}?`,
      body: `The ${noun} and every message in it will be deleted. This cannot be undone.`,
      confirmLabel: `Delete ${noun}`,
      danger: true,
    });
    if (!ok) return;
    try {
      await threadApi.remove(thread.id);
      useThreads.getState().remove(thread.id);
      onClose();
    } catch (err) {
      toast.error(errorMessage(err, `Could not delete the ${noun}.`));
    }
  };

  const items: MenuEntry[] = [
    canManage && { label: `Rename ${noun}`, icon: <Pencil size={16} />, onSelect: () => setRenaming(true) },
    canManage && isPost && (parent?.forumTags.length ?? 0) > 0 && { label: 'Change tags', icon: <Tag size={16} />, onSelect: () => setEditingTags(true) },
    canManage && {
      label: thread.archivedAt ? `Reopen ${noun}` : `Archive ${noun}`,
      icon: thread.archivedAt ? <Undo2 size={16} /> : <Archive size={16} />,
      onSelect: () => void update({ archived: !thread.archivedAt }),
    },
    canDelete && 'separator',
    canDelete && { label: `Delete ${noun}`, icon: <Trash2 size={16} />, danger: true, onSelect: () => void remove() },
  ];

  return (
    <aside
      aria-label={`${isPost ? 'Post' : 'Thread'}: ${thread.name}`}
      data-cf="thread-panel"
      className={cn('flex min-h-0 min-w-0 flex-col bg-surface', asPage ? 'flex-1' : 'w-[min(440px,45%)] shrink-0 border-l border-line')}
    >
      <header className="flex h-16 shrink-0 items-center gap-2 px-3 sm:px-4">
        {asPage ? (
          <IconButton label={`Back to #${parentName}`} onClick={onClose}>
            <ArrowLeft size={20} />
          </IconButton>
        ) : (
          isPost ? <Forum size={20} className="shrink-0 text-accent" /> : <Threads size={20} className="shrink-0 text-accent" />
        )}
        <div className="min-w-0 flex-1 leading-tight">
          {renaming ? (
            <RenameField thread={thread} onDone={() => setRenaming(false)} onSave={(name) => update({ name })} />
          ) : (
            <h2 className="truncate text-lg text-fg">{thread.name}</h2>
          )}
          <p className="truncate text-xs text-fg-muted">
            {asPage && <Threads size={11} className="mr-1 inline align-[-1px]" />}
            {isPost ? 'Post' : 'Thread'} in #{parentName}
            {thread.archivedAt && ' · Archived'}
          </p>
        </div>
        {items.some(Boolean) && (
          <>
            <IconButton ref={moreRef} label={`${isPost ? 'Post' : 'Thread'} options`} active={menuOpen} onClick={() => setMenuOpen((v) => !v)}>
              <MoreHorizontal size={20} />
            </IconButton>
            <Menu open={menuOpen} onClose={() => setMenuOpen(false)} anchor={moreRef} placement="bottom-end" items={items} label={`${isPost ? 'Post' : 'Thread'} options`} />
          </>
        )}
        {!asPage && (
          <IconButton label={`Close ${noun}`} onClick={onClose}>
            <X size={20} />
          </IconButton>
        )}
      </header>
      {isPost && parent && (thread.tagIds.length > 0 || editingTags) && (
        <div className="mx-3 mb-1 flex flex-wrap items-center gap-1.5 sm:mx-4">
          {editingTags
            ? parent.forumTags.map((tag) => (
                <TagChip
                  key={tag.id}
                  tag={tag}
                  active={thread.tagIds.includes(tag.id)}
                  onToggle={() => void update({ tagIds: thread.tagIds.includes(tag.id) ? thread.tagIds.filter((id) => id !== tag.id) : [...thread.tagIds, tag.id] })}
                />
              ))
            : thread.tagIds.map((id) => {
                const tag = parent.forumTags.find((t) => t.id === id);
                return tag ? <TagChip key={id} tag={tag} /> : null;
              })}
          {editingTags && (
            <button type="button" onClick={() => setEditingTags(false)} className="ml-1 text-xs font-semibold text-link hover:underline">
              Done
            </button>
          )}
        </div>
      )}
      {thread.archivedAt && (
        <p className="mx-3 mb-1 flex items-center gap-2 rounded-lg bg-sunken px-3 py-2 text-xs text-fg-muted sm:mx-4">
          <Archive size={14} className="shrink-0" /> This {noun} is archived. Sending a message reopens it.
        </p>
      )}
      <ChatView key={thread.id} target={{ channelId: thread.id }} serverId={thread.serverId} className="min-h-0 flex-1" />
    </aside>
  );
}

function RenameField({ thread, onDone, onSave }: { thread: ThreadDTO; onDone: () => void; onSave: (name: string) => Promise<boolean> }) {
  const [value, setValue] = useState(thread.name);
  const [saving, setSaving] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => input.current?.select(), []);

  const save = async () => {
    const name = value.trim();
    if (!name || name === thread.name) return onDone();
    setSaving(true);
    const ok = await onSave(name);
    setSaving(false);
    if (ok) onDone();
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onDone();
    }
  };
  return (
    <input
      ref={input}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={onKeyDown}
      onBlur={() => void save()}
      disabled={saving}
      maxLength={LIMITS.threadNameMax}
      aria-label="Thread name"
      className="h-7 w-full rounded-md border border-accent bg-sunken px-2 text-base text-fg focus:outline-none"
    />
  );
}
